import { useMemo, useState } from "react";

import { CheckIcon, MailIcon, XIcon } from "@lucide/icons";

import { formatDate } from "simplydo/core/dates";
import { Button } from "simplydo/react/ui/button";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";
import { Modal, ModalContent, ModalHeader } from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import { useAppSelector } from "store";
import util from "utils/utils";

import type { CompanyMessage } from "components/innointel/types";
import { UserChip } from "components/lib/Chips";

import { InfoBoxWidget, InfoBoxWidgetTitleArea } from "../Styles";

type CompanyMessagesProps = { messages: CompanyMessage[] };

const CompanyMessages = ({ messages }: CompanyMessagesProps) => {
  const [selectedMessage, setSelectedMessage] = useState<CompanyMessage | null>(
    null,
  );
  const allMessagesWithLogs = useMemo(
    () => messages.filter((message) => !!message.emailLogs?.length),
    [messages],
  );

  const user = useAppSelector((state) => state.user);
  const organisationId = user?.ownerOrganisation?._id;
  const canListUsers =
    user && util.hasPermission(user, "org.listUsers", organisationId);

  return (
    <>
      <Modal
        open={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        closeOnDimmerClick
      >
        <ModalHeader>{selectedMessage?.subject}</ModalHeader>
        <ModalContent>
          <p>{selectedMessage?.message}</p>
        </ModalContent>
      </Modal>
      <InfoBoxWidget $noBox>
        <InfoBoxWidgetTitleArea>
          <Icon icon={MailIcon} />
          <div>
            <H3>Messages</H3>
            <span>{messages.length} total</span>
          </div>
        </InfoBoxWidgetTitleArea>
        <p>
          These are communications sent by you or someone working in your
          organisation to this company.
        </p>
        <div style={{ overflowX: "auto", maxWidth: "100%" }}>
          {messages.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Subject</TableHeaderCell>
                  {canListUsers ? (
                    <TableHeaderCell>From</TableHeaderCell>
                  ) : null}
                  <TableHeaderCell>Reply-To address</TableHeaderCell>
                  <TableHeaderCell>Date</TableHeaderCell>
                  {allMessagesWithLogs.length > 0 ? (
                    <>
                      <TableHeaderCell collapsing>Sent</TableHeaderCell>
                      <TableHeaderCell collapsing>Delivered</TableHeaderCell>
                      <TableHeaderCell collapsing>Opened</TableHeaderCell>
                      <TableHeaderCell collapsing>Bounced</TableHeaderCell>
                    </>
                  ) : null}
                  <TableHeaderCell></TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((message) => {
                  const messageLog = message.emailLogs?.[0];
                  return (
                    <TableRow key={message._id}>
                      <TableCell>{message.subject}</TableCell>
                      {canListUsers ? (
                        <TableCell collapsing>
                          {message.ownerUser ? (
                            <UserChip user={message.ownerUser} />
                          ) : null}
                        </TableCell>
                      ) : null}
                      <TableCell collapsing>{message.replyTo}</TableCell>
                      <TableCell collapsing>
                        {formatDate(message.createdAt)}
                      </TableCell>
                      {allMessagesWithLogs.length > 0 ? (
                        <>
                          <TableCell>
                            {messageLog?.sent ? (
                              <Icon icon={CheckIcon} color="green" />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {messageLog?.events?.delivered ? (
                              <Icon icon={CheckIcon} color="green" />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {messageLog?.events?.open ? (
                              <Icon icon={CheckIcon} color="green" />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {messageLog?.events?.bounced ? (
                              <Icon icon={XIcon} color="red" />
                            ) : null}
                          </TableCell>
                        </>
                      ) : null}

                      <TableCell collapsing>
                        <Button onClick={() => setSelectedMessage(message)}>
                          View message
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Message>
              <p>No messages have been sent to this company yet.</p>
            </Message>
          )}
        </div>
      </InfoBoxWidget>
    </>
  );
};

export default CompanyMessages;
